import { getJwtExpiryMs, maskToken } from './jwt.js';

// Proactive scoped-token refresh.
//
// The host mints the token once per iframe creation (HOST_APP_CONVENTIONS
// §3.2), so a long session outlives it. We decode `exp` and ask the host for a
// new one shortly before it lapses.
//
// Deliberately PROACTIVE ONLY. We do not retry failed requests on 401/500:
// the proxy answers 500 both for an expired token and for a genuine server
// fault, so a reactive retry loop would turn an outage into a hammering loop
// and hide it. `refreshNow()` is exported so a reactive path can be added
// later behind a real signal.

const DEFAULT_LEAD_MS = 5 * 60 * 1000;   // refresh 5 minutes before expiry
const DEFAULT_MIN_DELAY_MS = 30 * 1000;  // never schedule tighter than this
const DEFAULT_RETRY_MS = 30 * 1000;      // first backoff after a failure
const MAX_RETRY_MS = 10 * 60 * 1000;     // backoff ceiling
// Refresh on tab wake if the token expires within this window
const WAKE_THRESHOLD_MS = DEFAULT_LEAD_MS;

/**
 * @param {object} deps
 * @param {() => string} deps.getToken            current token
 * @param {(token: string) => void} deps.setToken store the rotated token
 * @param {{ available: boolean, requestScopedToken: Function }} deps.bridge
 * @param {number} [deps.leadMs]
 * @param {number} [deps.minDelayMs]
 * @param {() => number} [deps.now]
 * @param {Window} [deps.win]
 * @returns {{ start: Function, stop: Function, refreshNow: Function }}
 */
export function createTokenRefresher({
  getToken,
  setToken,
  bridge,
  leadMs = DEFAULT_LEAD_MS,
  minDelayMs = DEFAULT_MIN_DELAY_MS,
  now = Date.now,
  win = typeof window !== 'undefined' ? window : undefined
}) {
  let timer = null;
  let retryMs = DEFAULT_RETRY_MS;
  let running = false;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /** Milliseconds until we want to refresh the given token. */
  const delayFor = (token) => {
    const expiryMs = getJwtExpiryMs(token);
    // No readable exp: fall back to a long poll rather than hammering
    if (expiryMs === null) return Math.max(minDelayMs, leadMs);
    return Math.max(minDelayMs, expiryMs - leadMs - now());
  };

  const schedule = () => {
    clearTimer();
    if (!running) return;
    const delay = delayFor(getToken());
    timer = setTimeout(() => { refreshNow(); }, delay);
  };

  /**
   * Ask the host for a token right now. Resolves true when one was stored.
   * @returns {Promise<boolean>}
   */
  async function refreshNow() {
    if (!bridge || !bridge.available) return false;

    try {
      const reply = await bridge.requestScopedToken();

      if (reply && reply.token) {
        setToken(reply.token);
        retryMs = DEFAULT_RETRY_MS;
        console.info(`Scoped token refreshed (${maskToken(reply.token)})`);
        // Re-arm from the NEW token's expiry
        schedule();
        return true;
      }

      // Timed out or the host declined — back off and try again
      console.warn('Scoped token refresh returned no token; retrying later');
    } catch (error) {
      console.warn('Scoped token refresh failed:', error.message);
    }

    clearTimer();
    if (running) {
      timer = setTimeout(() => { refreshNow(); }, retryMs);
      retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
    }
    return false;
  }

  // A backgrounded tab's timers are throttled or coalesced, so the scheduled
  // refresh can be very late. On wake, refresh at once if we are close to (or
  // past) expiry.
  const onVisibilityChange = () => {
    if (!running || !win || win.document.visibilityState !== 'visible') return;
    const expiryMs = getJwtExpiryMs(getToken());
    if (expiryMs === null) return;
    if (expiryMs - now() <= WAKE_THRESHOLD_MS) {
      refreshNow();
    } else {
      schedule();
    }
  };

  const start = () => {
    if (running) return;
    running = true;
    if (win) win.addEventListener('visibilitychange', onVisibilityChange);
    schedule();
  };

  const stop = () => {
    running = false;
    clearTimer();
    if (win) win.removeEventListener('visibilitychange', onVisibilityChange);
  };

  return { start, stop, refreshNow };
}
